import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { t } from "@/i18n";
import { AppLayout } from "@/components/AppLayout";
import { EditCompanyBasicsModal } from "@/components/EditCompanyBasicsModal";
import { Send, Sparkles, CheckCircle2, ArrowRight, Loader2, Pencil } from "lucide-react";
import _normalizeIntake from "@/utils/normalizeIntakeMessage";

// Human-friendly labels per question_key. Falls back to the generic
// placeholder key when no explicit label is defined. Keeps ondernemer
// crystal-clear about WHAT they're being asked to type.
const QUESTION_LABEL_NL = {
  company_name: { label: "Bedrijfsnaam", placeholder: "Bijv. Vloerspecialist Amsterdam BV" },
  website: { label: "Website", placeholder: "Bijv. voorbeeld.nl of https://voorbeeld.nl", optional_hint: "optioneel geldig webadres", ask_override: "Wat is het adres van uw website?" },
  industry: { label: "Branche", placeholder: "Kies of typ uw branche" },
  business_model: { label: "Verkoopmodel", placeholder: "bedrijven, particulieren, via andere bedrijven aan particulieren, of bedrijven én particulieren" },
  company_size: { label: "Bedrijfsgrootte", placeholder: "Aantal medewerkers" },
  products_services: { label: "Producten of diensten", placeholder: "Waar u vooral op verdient" },
  margins_note: { label: "Marge (optioneel)", placeholder: "Indicatie of prijsklasse" },
  target_audience: { label: "Doelgroep", placeholder: "Typ uw doelgroep" },
  ideal_customer: { label: "Ideale klant", placeholder: "Wat voor klant maakt u blij?" },
  unwanted_customers: { label: "Ongewenste klanten", placeholder: "Type klanten die u liever mijdt" },
  regions: { label: "Regio's", placeholder: "Waar u werkt" },
  capacity_new_customers: { label: "Nieuwe klanten per maand", placeholder: "Bijv. 15-20 projecten" },
  seasons_note: { label: "Seizoenen", placeholder: "Piek- of rustperiodes" },
  growth_goals: { label: "Groeidoel", placeholder: "Belangrijkste doel dit jaar" },
  revenue_goals: { label: "Omzetdoel (optioneel)", placeholder: "Concreet doel of leeg laten" },
  marketing_goals: { label: "Marketingdoel", placeholder: "Wat wilt u met marketing bereiken?" },
  competitors_text: { label: "Concurrenten", placeholder: "Naam, website — gescheiden door komma of enter" },
  brand_tone: { label: "Schrijfstijl", placeholder: "Hoe klinkt uw merk?" },
  brand_style: { label: "Stijl", placeholder: "Typerende stijlelementen" },
  brand_values: { label: "Kernwaarden", placeholder: "De belangrijkste waarden" },
  monthly_budget: { label: "Maandbudget", placeholder: "Bijv. €5.000 – €15.000" },
  risk_appetite: { label: "Risicoprofiel", placeholder: "Conservatief, Gebalanceerd of Agressief" },
};

export default function IntakePage() {
  const { refresh } = useAuth();
  const [state, setState] = useState(null);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [error, setError] = useState(null);
  const [validationError, setValidationError] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/intake/state")
      .then((r) => setState(r.data))
      .catch((e) => setError(e?.response?.data?.detail || t("common.error_generic")));
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    // Auto-focus the chat input on each new question so keyboard input
    // "just works" — no click required to start typing.
    if (inputRef.current && !completing) inputRef.current.focus();
  }, [state, completing]);

  // RESUME/EDIT: when the wizard resumes on a question that already has a
  // stored answer (e.g. user reloaded the browser, or a partial intake was
  // loaded), pre-fill the input with that stored value so the user can
  // overwrite/edit it. Anything the user has already TYPED but not yet sent
  // in the current session wins over the stored value (avoids clobbering
  // active input on background state refetches).
  const currentKey = state?.current_question?.question_key;
  const storedAnswer = currentKey ? state?.answers?.[currentKey] : undefined;
  useEffect(() => {
    if (!currentKey) return;
    if (!storedAnswer) return;
    // Only pre-fill when the input is empty (don't stomp on user's typing)
    setInput((cur) => (cur === "" ? String(storedAnswer) : cur));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentKey, storedAnswer]);

  // Per-question client-side validation. Backend already enforces min_length=1
  // for a non-empty message; this adds friendly Dutch NL guardrails for the
  // two identity fields Maurice reported struggling with.
  const validateForCurrentQuestion = (text, cq) => {
    const value = (text || "").trim();
    if (!value) return t("common.error_generic");
    const key = cq?.question_key;
    if (key === "company_name" && value.length < 2) {
      return "Bedrijfsnaam moet minimaal 2 tekens bevatten.";
    }
    if (key === "website") {
      // Accept: empty (already blocked above), plain domain, or full URL.
      // Regex: optional http(s):// prefix, then domain.tld[/path].
      const domainRe = /^(https?:\/\/)?([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}(\/.*)?$/i;
      if (!domainRe.test(value)) {
        return "Vul een geldig webadres in (bijv. voorbeeld.nl of https://voorbeeld.nl).";
      }
    }
    if (key === "monthly_budget") {
      // Negatieve bedragen tegenhouden. `0` blijft geldig als bewuste
      // "geen advertentiebudget"-keuze. Detectie op tekst i.p.v.
      // parseFloat zodat "€-100", "- 100", "-100 euro" allemaal
      // gevangen worden.
      if (/-\s*\d/.test(value)) {
        return t("intake.errors.budget_negative");
      }
    }
    return null;
  };

  const send = async (text) => {
    if (!text?.trim() || busy) return;
    const cq = state?.current_question;
    // Only validate when we know the current question. Skip validation for
    // completion / edge states.
    if (cq) {
      const err = validateForCurrentQuestion(text, cq);
      if (err) { setValidationError(err); return; }
    }
    setValidationError(null);
    setBusy(true); setError(null);
    setState((s) => s ? { ...s, messages: [...s.messages, { role: "user", content: text }] } : s);
    setInput("");
    try {
      const { data } = await api.post("/intake/answer", { message: text.trim() });
      setState(data);
      if (data.current_question === null && !data.completed) {
        setCompleting(true);
        try {
          await api.post("/intake/complete");
          await refresh();
        } finally {
          setCompleting(false);
        }
      }
    } catch (e) {
      setError(e?.response?.data?.detail || t("common.error_generic"));
    } finally {
      setBusy(false);
    }
  };

  // Chip click: FILL the input so the user can edit before submitting.
  // Previously chips auto-submitted, which felt "locked" and gave the user
  // no way to correct a suggested answer. This restores editability while
  // keeping chips as a fast suggestion mechanism.
  const applyChip = (chip) => {
    setValidationError(null);
    setInput(chip);
    if (inputRef.current) inputRef.current.focus();
  };

  if (!state) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center py-24 text-text-muted">
          <Loader2 className="w-4 h-4 animate-spin mr-2" /> {t("common.loading")}
        </div>
      </AppLayout>
    );
  }

  const done = state.completed || (state.current_question === null && Object.keys(state.answers).length > 0);
  const cq = state.current_question;

  return (
    <AppLayout>
      <div className="grid lg:grid-cols-[1fr_320px] gap-6">
        {/* Chat column — bounded height so the input footer is ALWAYS in view
            (verified at 1366×768). min-h keeps it usable on tall screens;
            max-h prevents overflow off-screen on lower-res laptops. */}
        <div className="rounded-xl border border-border-hairline bg-bg-panel overflow-hidden flex flex-col min-h-[420px] max-h-[calc(100vh-8rem)]">
          <div className="px-6 py-4 border-b border-border-hairline flex items-center justify-between">
            <div>
              <h1 className="text-lg font-bold text-text-primary tracking-tight" data-testid="intake-title">
                {t("intake.title")}
              </h1>
              <p className="text-xs text-text-muted mt-0.5">{t("intake.subtitle")}</p>
            </div>
            <ProgressPill percent={state.progress.percent} />
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-6 space-y-4 bg-bg-panel">
            {state.messages.map((m, i) => (
              <MessageBubble key={i} role={m.role} content={m.content} />
            ))}
            {busy && (
              <div className="flex items-center gap-2 text-text-muted text-sm">
                <span className="flex gap-1">
                  <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                </span>
                {t("intake.typing")}
              </div>
            )}
            {error && <div className="text-neg-red text-sm">{error}</div>}
          </div>

          {done ? (
            <div className="p-6 border-t border-border-hairline bg-pos-green/10">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-pos-green mt-0.5" />
                <div className="flex-1">
                  <div className="font-semibold text-text-primary">{t("intake.complete_title")}</div>
                  <p className="text-sm text-text-muted mt-1">{t("intake.complete_body")}</p>
                </div>
                <button
                  onClick={() => nav("/privacy")}
                  data-testid="intake-complete-cta"
                  className="h-9 px-4 rounded-md bg-accent-teal hover:bg-accent-teal-dim text-white text-sm font-semibold flex items-center gap-2"
                >
                  {t("intake.to_privacy")} <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ) : (
            <div className="p-4 border-t border-border-hairline bg-bg-panel">
              {(() => {
                const meta = QUESTION_LABEL_NL[cq?.question_key] || null;
                if (!meta) return null;
                return (
                  <div className="mb-2 flex items-baseline justify-between gap-2">
                    <label htmlFor="intake-chat-input" className="text-xs text-text-muted font-semibold">
                      {meta.label}
                    </label>
                    {cq?.optional && (
                      <span className="text-[10px] text-text-subtle italic">{t("common.optional")}</span>
                    )}
                  </div>
                );
              })()}
              {cq?.chips?.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {cq.chips.map((chip) => (
                    <button
                      key={chip}
                      onClick={() => applyChip(chip)}
                      disabled={busy}
                      data-testid={`intake-chip-${chip.slice(0, 20).replace(/\s+/g, "-").toLowerCase()}`}
                      className="px-3 h-8 rounded-full border border-info-blue/40 bg-info-blue/10 hover:bg-info-blue/20 text-accent-teal text-xs font-medium transition-colors disabled:opacity-50"
                    >
                      {chip}
                    </button>
                  ))}
                </div>
              )}
              <form
                onSubmit={(e) => { e.preventDefault(); send(input); }}
                className="flex items-center gap-2"
              >
                <input
                  id="intake-chat-input"
                  ref={inputRef}
                  value={input}
                  onChange={(e) => { setInput(e.target.value); if (validationError) setValidationError(null); }}
                  placeholder={(QUESTION_LABEL_NL[cq?.question_key]?.placeholder) || t("intake.placeholder")}
                  disabled={busy}
                  autoFocus
                  data-testid="intake-chat-input"
                  className="flex-1 h-11 px-4 rounded-md bg-bg-panel border border-border-hairline text-text-primary placeholder:text-text-subtle focus:outline-none focus:border-accent-teal focus:ring-2 focus:ring-accent-teal/30"
                />
                <button
                  type="submit"
                  disabled={busy || !input.trim()}
                  data-testid="intake-send-button"
                  className="h-11 px-4 rounded-md bg-accent-teal hover:bg-accent-teal-dim disabled:opacity-50 text-white font-semibold flex items-center gap-2"
                >
                  {t("intake.send")} <Send className="w-3.5 h-3.5" />
                </button>
              </form>
              {validationError && (
                <div data-testid="intake-validation-error" className="mt-2 text-xs text-neg-red">
                  {validationError}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sidebar */}
        <aside className="space-y-4">
          <div className="rounded-xl border border-border-hairline bg-bg-panel p-4">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-accent-teal" />
              <h3 className="text-xs text-text-muted font-semibold">Secties</h3>
            </div>
            <ol className="space-y-1">
              {state.sections.map((s) => {
                const isCurrent = s.idx === state.section_idx;
                const isDone = s.idx < state.section_idx || state.completed;
                return (
                  <li
                    key={s.key}
                    className={`flex items-center gap-2 text-sm px-2 py-1.5 rounded ${ isCurrent ? "bg-info-blue/10 text-accent-teal font-medium" : isDone ? "text-text-primary/85" : "text-text-subtle" }`}
                  >
                    <span className={`w-4 h-4 rounded-full border flex items-center justify-center text-[10px] font-bold ${ isDone ? "bg-pos-green/20 border-emerald-400 text-pos-green" : isCurrent ? "border-blue-500 text-accent-teal bg-bg-panel" : "border-border-hairline text-text-subtle bg-bg-panel" }`}>
                      {isDone ? "✓" : s.idx + 1}
                    </span>
                    <span>{s.title_nl}</span>
                  </li>
                );
              })}
            </ol>
          </div>
          <div className="rounded-xl border border-border-hairline bg-bg-panel p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs text-text-muted font-semibold">JustEasySEO leest mee</h3>
              {(state.answers.company_name || state.answers.website) && (
                <button
                  data-testid="intake-edit-company-btn"
                  onClick={() => setEditOpen(true)}
                  className="flex items-center gap-1 text-[10px] text-accent-teal hover:text-info-blue"
                  title="Bedrijfsnaam / website wijzigen"
                >
                  <Pencil className="w-3 h-3" /> Wijzig
                </button>
              )}
            </div>
            <div className="space-y-2 max-h-[280px] overflow-y-auto">
              {Object.keys(state.answers).length === 0 ? (
                <p className="text-xs text-text-subtle">Antwoorden verschijnen hier.</p>
              ) : (
                Object.entries(state.answers).map(([k, v]) => {
                  const upper = String(k).toUpperCase();
                  const label = t(`intake_field.${upper}`);
                  const nice = label && !label.startsWith("intake_field.") ? label : k;
                  const upperVal = String(v ?? "").toUpperCase();
                  const valMap = t(`intake_field_value.${upperVal}`);
                  let niceVal = valMap && !valMap.startsWith("intake_field_value.") ? valMap : v;
                  // M124-e: negatief opgeslagen `monthly_budget` (bv. legacy
                  // "-100") → toon B1-NL fallback i.p.v. de corrupte waarde.
                  // Klantentekst wordt NIET herschreven; alleen de display
                  // in dit "JustEasySEO leest mee"-paneel toont de fallback.
                  const isNegBudget = (k === "monthly_budget")
                    && /-\s*\d/.test(String(v ?? ""));
                  return (
                    <div key={k} className="text-xs">
                      <div className="text-text-muted text-[10px]">{nice}</div>
                      {isNegBudget ? (
                        <>
                          <div className="text-text-primary line-clamp-2 italic">
                            {t("intake.display.budget_invalid")}
                          </div>
                          <div className="text-text-subtle text-[10px] mt-0.5">
                            Ongeldige eerder opgeslagen waarde.
                          </div>
                        </>
                      ) : (
                        <div className="text-text-primary line-clamp-2">{niceVal}</div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </aside>
      </div>
      <EditCompanyBasicsModal
        open={editOpen}
        initial={{ company_name: state.answers.company_name || "",
                   website: state.answers.website || "" }}
        onClose={() => setEditOpen(false)}
        onSaved={(profile) => {
          // Merge locally so the sidebar reflects the change instantly.
          setState((s) => s ? {
            ...s,
            answers: {
              ...s.answers,
              company_name: profile?.company_name ?? s.answers.company_name,
              website: profile?.website ?? s.answers.website,
            },
          } : s);
        }}
      />
    </AppLayout>
  );
}

function MessageBubble({ role, content }) {
  // JustEasySEO/system messages pass through the presentation-time normalizer
  // so legacy English product names and stray markdown asterisks in
  // already-saved DB content are rendered as helder Nederlands. User
  // messages are shown 1-on-1 without normalization.
  if (role !== "user") {
    const { html } = _normalizeIntake(content || "");
    return (
      <div className="max-w-[85%] rounded-xl px-4 py-3 mr-auto bg-bg-panel border border-border-hairline">
        <div className="text-sm text-text-primary leading-relaxed"
             dangerouslySetInnerHTML={{ __html: html }} />
      </div>
    );
  }
  return (
    <div className="max-w-[85%] rounded-xl px-4 py-3 ml-auto bg-accent-teal/15 border border-accent-teal/40">
      <div className="text-sm text-text-primary leading-relaxed whitespace-pre-wrap">
        {content}
      </div>
    </div>
  );
}

function ProgressPill({ percent }) {
  return (
    <div className="flex items-center gap-2">
      <div className="w-32 h-1.5 bg-bg-panel-raised rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-blue-500 to-blue-600 transition-all"
          style={{ width: `${percent}%` }}
        />
      </div>
      <span data-testid="intake-progress" className="text-xs text-text-muted min-w-[36px] text-right font-semibold">
        {percent}%
      </span>
    </div>
  );
}
